import { useLocation, useNavigate } from 'react-router-dom';
import { usePDIContext } from '../context/PDIContext';
import DeliveryBlockerBanner from './DeliveryBlockerBanner';
import OnboardingTour from './OnboardingTour';
import {
  ClipboardCheck,
  Home,
  FileText,
  Car,
  Sparkles
} from 'lucide-react';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading, toast, overallProgress } = usePDIContext();

  // The journey: learn → set up the car → inspect → get the report
  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/car-profile', icon: Car, label: 'Setup' },
    { path: '/checklist', icon: ClipboardCheck, label: 'Inspect' },
    { path: '/summary', icon: FileText, label: 'Report' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen hero-pattern flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <img src="/car-icon.svg" alt="Car PDI" className="w-16 h-16 rounded-2xl shadow-xl mx-auto" />
            <div className="absolute -inset-4 bg-gradient-to-r from-brand-400/30 to-teal-400/30 rounded-full blur-xl animate-pulse -z-10" />
          </div>
          <p className="mt-6 text-slate-600 font-medium">Loading your PDI...</p>
          <div className="mt-2 flex justify-center gap-1">
            <div className="w-2 h-2 bg-brand-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-brand-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 glass border-b border-slate-200/50 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => navigate('/')}
          >
            <div className="relative">
              <img src="/car-icon.svg" alt="Car PDI" className="w-10 h-10 rounded-xl shadow-lg group-hover:shadow-xl transition-shadow" />
              <div className="absolute -inset-1 bg-brand-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
            </div>
            <div>
              <h1 className="font-display font-bold text-slate-800 text-lg leading-tight flex items-center gap-1.5">
                Car PDI
                <Sparkles className="w-4 h-4 text-amber-500" />
              </h1>
              <p className="text-[10px] text-slate-500 font-medium tracking-wide">PRE-DELIVERY INSPECTION</p>
            </div>
          </div>
          
          {/* Progress indicator in header */}
          {location.pathname === '/checklist' && overallProgress.total > 0 && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-bold text-brand-600">
                  {overallProgress.completed}/{overallProgress.total}
                </p>
                <p className="text-[10px] text-slate-400 font-medium">ITEMS CHECKED</p>
              </div>
              <div className="w-14 h-14 relative">
                <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-slate-200"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-brand-500 score-ring"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={`${overallProgress.percentage}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-brand-600">
                  {overallProgress.percentage}%
                </span>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Delivery Blocker Banner */}
      {location.pathname !== '/summary' && <DeliveryBlockerBanner compact />}

      {/* Main Content */}
      <main className="flex-1 pb-24">
        {children}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 glass border-t border-slate-200/50 safe-bottom z-30">
        <div className="max-w-3xl mx-auto flex justify-around py-1">
          {navItems.map(({ path, icon: Icon, label }) => {
            const isActive = location.pathname === path;
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={`relative flex flex-col items-center py-2 px-4 min-w-[64px] transition-all duration-300 rounded-xl mx-1 ${
                  isActive 
                    ? 'text-brand-600' 
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {isActive && (
                  <div className="absolute inset-0 bg-brand-50 rounded-xl -z-10" />
                )}
                <div className={`relative ${isActive ? 'scale-110' : ''} transition-transform duration-200`}>
                  <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5px]' : ''}`} />
                  {isActive && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-brand-500 rounded-full" />
                  )}
                </div>
                <span className={`text-[10px] mt-1 ${isActive ? 'font-bold' : 'font-medium'}`}>
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Toast Notification */}
      {toast && (
        <div className="toast flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center">
            <ClipboardCheck className="w-3 h-3 text-white" />
          </div>
          {toast}
        </div>
      )}

      {/* Onboarding Tour - shows only on first visit */}
      <OnboardingTour />
    </div>
  );
};

export default Layout;
